import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import {useScroll, useTransform} from 'framer-motion'
import type {ReactNode} from 'react'
import {useEffect, useRef, useState} from 'react'
import {Container} from 'src/common/Container'
import {extractTextSegments} from 'src/common/helpers/extractTextSegments'

/**
 * Props for `StresstimonialsText`.
 */
export type StresstimonialsTextProps = SliceComponentProps<Content.StresstimonialsTextSlice>

/**
 * Component for "StresstimonialsText" Slices.
 */
const StresstimonialsText = ({slice}: StresstimonialsTextProps): JSX.Element => {
  const [charCount, setCharCount] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const segments = isFilled.richText(slice.primary.text) ? extractTextSegments(slice.primary.text) : []
  const totalChars = segments.reduce((acc, seg) => acc + seg.text.length, 0)

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Intersection Observer for mobile CSS animation trigger
  useEffect(() => {
    if (!isMobile || !containerRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting)
        })
      },
      {
        threshold: 0,
        rootMargin: '0px 0px -100px 0px', // top right bottom left
      },
    )

    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [isMobile])

  const {scrollYProgress} = useScroll({
    target: containerRef,
    offset: ['start 100%', 'start 90%'],
  })

  const rawCharCount = useTransform(scrollYProgress, [0, 1], [0, totalChars])
  const targetCharCountRef = useRef(0)

  // Track scroll target (desktop only)
  useEffect(() => {
    if (isMobile) {
      return
    }

    const unsubscribe = rawCharCount.on('change', (latest) => {
      targetCharCountRef.current = latest
    })

    return unsubscribe
  }, [rawCharCount, isMobile])

  // Animate towards target at constant speed (desktop only)
  useEffect(() => {
    if (isMobile) {
      return
    }

    let animationFrameId: number

    const animate = () => {
      setCharCount((prev) => {
        const target = targetCharCountRef.current
        const diff = target - prev

        if (Math.abs(diff) < 0.5) {
          return target
        }

        // Dynamic typing speed: start fast, slow down towards end
        // Calculate progress (0 = start, 1 = end)
        const safeTotalChars = totalChars || 1
        const progress = prev / safeTotalChars

        // Ease-out curve: starts fast, ends slow
        // Higher progress = closer to end = slower speed
        const easeFactor = 1 - progress * progress // Quadratic ease-out

        // Speed range: 0.5 (fast at start) to 0.15 (slow at end)
        const minSpeed = 0.15
        const maxSpeed = 0.25
        const charsPerFrame = minSpeed + easeFactor * (maxSpeed - minSpeed)

        return prev + Math.sign(diff) * Math.min(Math.abs(diff), charsPerFrame)
      })

      animationFrameId = requestAnimationFrame(animate)
    }

    animationFrameId = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(animationFrameId)
  }, [isMobile, totalChars])

  const renderTypewriterText = (): ReactNode => {
    // Mobile: show full text immediately, desktop: typewriter effect
    if (isMobile) {
      return segments.map((segment, segIndex) => {
        if (segment.text === '\n') {
          return <br key={segIndex} />
        }

        const element = segment.isBold ? (
          <strong key={segIndex} className="font-medium text-moss-black">
            {segment.text}
          </strong>
        ) : (
          <span key={segIndex}>{segment.text}</span>
        )

        return element
      })
    }

    // Desktop: typewriter effect
    let displayedChars = 0
    const elements: ReactNode[] = []

    segments.forEach((segment, segIndex) => {
      const segmentStart = displayedChars
      const segmentEnd = displayedChars + segment.text.length
      const visibleLength = Math.max(0, Math.min(charCount - segmentStart, segment.text.length))
      const visibleText = segment.text.slice(0, visibleLength)

      if (visibleText) {
        if (segment.text === '\n') {
          elements.push(<br key={segIndex} />)
        } else {
          const element = segment.isBold ? (
            <strong key={segIndex} className="font-medium text-moss-black">
              {visibleText}
            </strong>
          ) : (
            <span key={segIndex}>{visibleText}</span>
          )

          elements.push(element)
        }
      }

      displayedChars = segmentEnd
    })

    return elements
  }

  return (
    <Container
      backgroundColor="Transparent"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="!py-[30px] md:!py-[90px]"
    >
      <style>{`
        @keyframes caretBlink {
          0%, 100% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
        }

        .mobile-text-animated {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1),
                      transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .mobile-text-visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>
      <div
        ref={containerRef}
        className="mx-auto flex w-full max-w-[744px] items-center justify-center px-4 md:px-10 xl:px-0"
      >
        {isFilled.richText(slice.primary.text) && (
          <div className="relative w-full max-w-[1088px]">
            {/* Hidden full text to reserve space and prevent layout shift */}
            <p
              className="invisible text-center text-32 font-medium leading-[1.25] tracking-[-0.48px] text-moss-grey-400 md:text-48"
              aria-hidden="true"
            >
              {segments.map((segment, segIndex) => {
                if (segment.text === '\n') {
                  return <br key={segIndex} />
                }

                const element = segment.isBold ? (
                  <strong key={segIndex} className="font-medium text-moss-black">
                    {segment.text}
                  </strong>
                ) : (
                  <span key={segIndex}>{segment.text}</span>
                )

                return element
              })}
            </p>

            {/* Visible typewriter text */}
            <p
              className={cn(
                'absolute inset-0 text-center text-32 font-medium leading-[1.25] tracking-[-0.48px] text-moss-grey-400 md:text-48',
                {
                  'mobile-text-animated': isMobile,
                  'mobile-text-visible': isMobile && isVisible,
                },
              )}
            >
              {renderTypewriterText()}
              {!isMobile && charCount > 0 && charCount < totalChars && (
                <span
                  className="inline-block text-moss-grey-400"
                  style={{
                    animation: 'caretBlink 1s ease-in-out infinite',
                    pointerEvents: 'none',
                  }}
                >
                  |
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </Container>
  )
}

export default StresstimonialsText
